import { cn } from '@/helpers/utils/functionality-utils';
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import type { AppTopbarRef } from '@/types';
import { useTranslation } from 'react-i18next';
import ProfileMenu from '@/modules/global-components/ui/profile_menu';
import { useAppSelector } from '@/store/hooks';
import { LayoutContext } from './context/context';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { TenantInfo } from '@/modules/global-components/ui/TenantInfo';
import { LanguageMenu } from '@/modules/global-components/ui/LanguageMenu';
import { Button } from 'primereact/button';
import { AddTaskPopup } from './components/AddTaskPopup/AddTask';
import { UserNotifPanel } from './components/UserNotifPanel/UserNotifPanel';
import { TbLogout, TbMoon, TbSun } from 'react-icons/tb';
import { MobileMenu } from './MobileMenu';
import type { OverlayPanel } from 'primereact/overlaypanel';

const AppTopbar = forwardRef<AppTopbarRef>((_props, ref) => {
  const { t } = useTranslation('common');
  const { layoutState, onMenuToggle, theme, toggleTheme } =
    useContext(LayoutContext);
  const menubuttonRef = useRef(null);
  const topbarmenuRef = useRef(null);
  const topbarmenubuttonRef = useRef(null);

  const notificationRef = useRef<OverlayPanel>(null);
  const alarmsRef = useRef<OverlayPanel>(null);
  const tasksRef = useRef<OverlayPanel>(null);

  const urlGuid = useParams()?.code;

  const nav = useNavigate();
  const path = useLocation();
  const pathName = path.pathname;

  const authUser = useAppSelector((state) => state.user.authUserData.data);
  const isCustomer = !!authUser?.customer?.c_id;
  const customerGuid = authUser?.customer?.compu_id;
  const allowAssignTask = authUser?.tenant?.settings?.allow_assign_tasks;

  const [isOpenAddTaskPopup, setIsOpenAddTaskPopup] = useState(false);
  const [expandMobileMenu, setExpandMobileMenu] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const customerMenu = [
    {
      label: t('home'),
      icon: 'pi pi-home',
      to: `/main/${customerGuid}/dashboard`,
    },
    {
      label: t('Carriers'),
      icon: 'pi pi-car',
      to: `/main/${customerGuid}/carriers/list`,
    },
    {
      label: t('Invoices'),
      icon: 'pi pi-book',
      to: `/main/${customerGuid}/invoice/list`,
    },
    {
      label: t('Payments'),
      icon: 'pi pi-credit-card',
      to: `/main/${customerGuid}/payments/list`,
    },
    {
      label: t('transactions'),
      icon: 'pi pi-dollar',
      to: `/main/${customerGuid}/transaction`,
    },
  ];

  useImperativeHandle(ref, () => ({
    menubutton: menubuttonRef.current,
    topbarmenu: topbarmenuRef.current,
    topbarmenubutton: topbarmenubuttonRef.current,
  }));

  useEffect(() => {
    let redirect = false;

    if (isCustomer) {
      if (!urlGuid) {
        redirect = true;
      }
      if (pathName.includes('public')) redirect = false;
      if (redirect) nav(`${customerGuid}/dashboard`);
    } else {
      if (urlGuid) {
        redirect = true;
      }
      if (pathName.includes('public')) redirect = false;
      if (redirect) nav(`/main/dashboard`);
    }
  }, [path]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideMenu = mobileMenuRef.current?.contains(target);

      const isInside =
        isInsideMenu ||
        notificationRef.current?.getElement()?.contains(target) ||
        tasksRef.current?.getElement()?.contains(target) ||
        alarmsRef.current?.getElement()?.contains(target);

      function hideOverlay() {
        if (notificationRef.current?.isVisible()) {
          notificationRef.current.hide();
        }
        if (tasksRef.current?.isVisible()) {
          tasksRef.current.hide();
        }
        if (alarmsRef.current?.isVisible()) {
          alarmsRef.current.hide();
        }
      }

      if (isInsideMenu) {
        hideOverlay();
      }

      if (!isInside) {
        hideOverlay();
        setExpandMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className="layout-topbar">
        <div className="flex gap-2">
          <Link viewTransition to="/" className="layout-topbar-logo">
            <img
              src={`/assets/images/logo/logo-${
                theme === 'light' ? 'dark' : 'light'
              }-512.png`}
              style={{ height: '50px', width: '50px' }}
              width="50px"
              height="50px"
              alt="logo"
            />
            <span className="hidden sm:block">{t('Car Trade Suite')}</span>
          </Link>
          <button
            ref={menubuttonRef}
            type="button"
            className={cn(
              'p-link layout-menu-button layout-topbar-button',
              isCustomer && 'invisible',
            )}
            onClick={onMenuToggle}
          >
            <i className="pi pi-bars" />
          </button>
          <TenantInfo containerClassName="hidden sm:flex" />
          {isOpenAddTaskPopup && (
            <AddTaskPopup
              open={isOpenAddTaskPopup}
              setOpen={setIsOpenAddTaskPopup}
            />
          )}
          {!isCustomer && allowAssignTask && (
            <>
              <Button
                severity="info"
                raised
                type="button"
                rounded
                className="group ml-2 hidden h-fit border-none bg-gradient-to-r from-sky-600 to-sky-400 bg-cover bg-no-repeat p-3 px-4 md:block"
                onClick={() => setIsOpenAddTaskPopup(true)}
              >
                <div className="flex items-center text-white">
                  <i className="pi pi-plus"></i>
                  <div className="w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 ease-in-out group-hover:w-[100px] group-hover:pl-2 group-hover:opacity-100">
                    {t('assignTask')}
                  </div>
                </div>
              </Button>
              <Button
                severity="info"
                type="button"
                className="fixed bottom-10 right-10 block size-16 md:hidden"
                rounded
                onClick={() => setIsOpenAddTaskPopup(true)}
              >
                <div className="grid size-full place-items-center">
                  <i className="pi pi-plus text-xl"></i>
                </div>
              </Button>
            </>
          )}
        </div>
        <div ref={mobileMenuRef} className="relative">
          <button
            type="button"
            className="p-link layout-topbar-menu-button layout-topbar-button ml-0 mt-2 md:ml-auto md:mt-0"
            onClick={() => {
              setExpandMobileMenu(!expandMobileMenu);
            }}
          >
            <i className="pi pi-ellipsis-v" />
          </button>
          {expandMobileMenu && (
            <MobileMenu
              nr={notificationRef}
              tr={tasksRef}
              ar={alarmsRef}
              open={expandMobileMenu}
              setOpen={setExpandMobileMenu}
            />
          )}
        </div>
        <div
          ref={topbarmenuRef}
          className={cn('layout-topbar-menu', {
            'layout-topbar-menu-mobile-active':
              layoutState.profileSidebarVisible,
          })}
        >
          <LanguageMenu />
          {!isCustomer && (
            <UserNotifPanel nr={notificationRef} tr={tasksRef} ar={alarmsRef} />
          )}
          <button
            title={t('Switch Theme')}
            type="button"
            className="p-link layout-topbar-button"
            onClick={toggleTheme}
          >
            {theme === 'light' ? (
              <TbMoon className="mr-2 text-3xl sm:mr-0" />
            ) : (
              <TbSun className="mr-2 text-3xl sm:mr-0" />
            )}
            <span className="ml-2">{t('Switch Theme')}</span>
          </button>
          <ProfileMenu />
          <button
            type="button"
            className="p-link layout-topbar-button"
            onClick={() => {
              nav('/blank', {
                state: {
                  type: 'logout',
                },
              });
            }}
            title={t('Sign out')}
          >
            <TbLogout className="mr-2 text-3xl sm:mr-0" />
            <span className="ml-2">{t('Sign out')}</span>
          </button>
        </div>
      </div>
      {isCustomer && (
        <>
          <div className="h-[70px] w-full" />
          <div
            className={cn(
              'layout-topbar !left-1/2 !top-20 !h-[70px] !w-screen !-translate-x-1/2 sm:!max-w-[700px] sm:rounded-full',
              layoutState.profileSidebarVisible && '!z-50',
            )}
          >
            <div className="flex size-full items-center justify-between gap-3">
              {customerMenu.map((item) => {
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'flex h-10 shrink-0 items-center gap-2 border-b border-transparent font-semibold !text-surface-600',
                      item.to === pathName && 'border-teal-500 !text-teal-500',
                    )}
                  >
                    <i className={'text-3xl sm:text-base ' + item.icon}></i>
                    <span className={cn('hidden text-xl sm:block')}>
                      {t(item.label)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
});

AppTopbar.displayName = 'AppTopbar';

export default AppTopbar;
